import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GetAllStatuses } from "src/apis/loopupListApis";
import { GetAllVehicleMakes } from "src/apis/MakeApis";
import { GetModels } from "src/apis/ModelApis";
import StatusObjecType from "src/common_constants/Types";

const CACHE_EXPIRY = 30 * 60;

interface AssetFilterData {
  makes: any[];
  models: any[];
  statuses: any[];
}

export interface AssetFiltersState {
  data: AssetFilterData | null;
  lastFetched: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: AssetFiltersState = {
  data: null,
  lastFetched: null,
  loading: false,
  error: null,
};

export const fetchAssetFilters = createAsyncThunk<
  AssetFilterData,
  { clientId: string | null },
  { rejectValue: string }
>(
  "assetFilters/fetchAssetFilters",
  async ({ clientId }, { getState, rejectWithValue }) => {
    const state = (getState() as any).assetFilters;
    const now = Math.floor(Date.now() / 1000);

    if (
      state.data &&
      state.lastFetched &&
      now - state.lastFetched < CACHE_EXPIRY
    ) {
      return state.data;
    }

    if (!clientId) {
      return rejectWithValue("Client ID is required");
    }

    try {
      const [makesResp, modelsResp, statusResp] = await Promise.all([
        GetAllVehicleMakes(clientId),
        GetModels(clientId),

        GetAllStatuses(StatusObjecType.VEHICLEASSET),
      ]);

      const filterData: AssetFilterData = {
        makes: makesResp.data || [],
        models: modelsResp.data || [],
        statuses: statusResp.data || [],
      };

      return filterData;
    } catch (error) {
      console.error("Error fetching asset filters:", error);
      return rejectWithValue("Failed to fetch filter data");
    }
  }
);

const assetFiltersSlice = createSlice({
  name: "assetFilters",
  initialState,
  reducers: {
    clearAssetFilters: (state) => {
      state.data = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssetFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAssetFilters.fulfilled,
        (state, action: PayloadAction<AssetFilterData>) => {
          state.loading = false;
          state.data = action.payload;
          state.lastFetched = Math.floor(Date.now() / 1000);
          state.error = null;
        }
      )
      .addCase(fetchAssetFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unknown error";
      });
  },
});

export const { clearAssetFilters } = assetFiltersSlice.actions;
export default assetFiltersSlice.reducer;

export const selectAssetFilterData = (state: any) => state.assetFilters.data;
export const selectAssetFilterLoading = (state: any) =>
  state.assetFilters.loading;
export const selectAssetFilterLastFetched = (state: any) =>
  state.assetFilters.lastFetched;
